import './polyfills';

// 🛡️ Polyfill de crypto.randomUUID para WebViews antigas (Xiaomi / Redmi / Oppo Android 8-11)
if (typeof window !== 'undefined') {
  if (!window.crypto) {
    (window as any).crypto = {};
  }
  if (!window.crypto.randomUUID) {
    window.crypto.randomUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };
  }
}

// 🛡️ Safe localStorage & sessionStorage fallback to prevent crashes in private browsing and restrictive WebViews (e.g. Instagram/WhatsApp/Mi Browser)
const installStorageFallback = (storageName: "localStorage" | "sessionStorage") => {
  try {
    const testKey = "__storage_test__";
    if (window[storageName]) {
      window[storageName].setItem(testKey, testKey);
      window[storageName].removeItem(testKey);
    } else {
      throw new Error("Storage object is missing");
    }
  } catch (e) {
    console.warn(`[Storage Fallback] Native ${storageName} is unavailable. Installing safe in-memory fallback.`);
    const store = new Map<string, string>();
    const mockStorage = {
      getItem: (key: string) => store.get(key) || null,
      setItem: (key: string, value: string) => { store.set(key, value); },
      removeItem: (key: string) => { store.delete(key); },
      clear: () => { store.clear(); },
      key: (index: number) => Array.from(store.keys())[index] || null,
      get length() { return store.size; }
    };
    
    try {
      Object.defineProperty(window, storageName, {
        value: mockStorage,
        configurable: true,
        enumerable: true,
        writable: true
      });
    } catch (defineErr) {
      try {
        if (typeof Storage !== 'undefined' && Storage.prototype) {
          const proto = Storage.prototype;
          proto.setItem = function(key, val) { store.set(key, val); };
          proto.getItem = function(key) { return store.get(key) || null; };
          proto.removeItem = function(key) { store.delete(key); };
          proto.clear = function() { store.clear(); };
        }
      } catch (protoErr) {
        console.warn(`[Storage Fallback] Could not patch Storage prototype`, protoErr);
      }
    }
  }
};

installStorageFallback("localStorage");
installStorageFallback("sessionStorage");

import 'resize-observer-polyfill/dist/ResizeObserver.global';
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// 🧹 Auto-purge outdated SW caches when a new build version is detected
const CURRENT_APP_VERSION = "2026.10.23.v4";
try {
  const cachedVersion = localStorage.getItem("app_build_version");
  if (cachedVersion !== CURRENT_APP_VERSION) {
    localStorage.setItem("app_build_version", CURRENT_APP_VERSION);
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          console.log("[Version Purge] Deletando cache antigo:", name);
          caches.delete(name);
        });
      });
    }
  }
} catch (e) {}

// 🔄 Auto-recovery for outdated Service Worker cache / chunk loading errors in embedded WebViews
window.addEventListener('error', (event) => {
  if (event.message && (event.message.includes('Loading chunk') || event.message.includes('Failed to fetch dynamically imported module'))) {
    console.warn("[Chunk Load Error] Stale cache detected in WebView. Refreshing without service worker...");
    const reloadKey = "__recovered_chunk_error__";
    try {
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach(r => r.unregister());
            window.location.reload();
          }).catch(() => window.location.reload());
        } else {
          window.location.reload();
        }
      }
    } catch (e) {
      window.location.reload();
    }
  }
});

// In development mode, unregister any stale service workers to prevent cached white-screens
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  }).catch(() => {});
}

const rootElement = document.getElementById("root");
if (rootElement) {
  try {
    createRoot(rootElement).render(<App />);
  } catch (err: any) {
    console.error("[FATAL BOOT ERROR]", err);
    rootElement.innerHTML = `
      <div style="min-height:100vh;background:#0d0d0d;color:#ffffff;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;text-align:center;font-family:sans-serif;">
        <div style="width:56px;height:56px;border-radius:50%;background:rgba(255,106,0,0.15);display:flex;align-items:center;justify-content:center;margin-bottom:16px;">
          <span style="font-size:28px;">⚡</span>
        </div>
        <h2 style="font-size:18px;font-weight:800;margin-bottom:8px;color:#ff6a00;text-transform:uppercase;letter-spacing:1px;">Atualização Necessária</h2>
        <p style="font-size:13px;color:rgba(255,255,255,0.65);max-width:320px;margin-bottom:24px;line-height:1.5;">
          Houve uma atualização recente no sistema. Toque abaixo para sincronizar seu aparelho:
        </p>
        <button onclick="try{localStorage.clear();sessionStorage.clear();}catch(e){};if('serviceWorker' in navigator){navigator.serviceWorker.getRegistrations().then(r=>r.forEach(w=>w.unregister())).finally(()=>window.location.reload());}else{window.location.reload();}" style="background:#ff6a00;color:#000000;border:none;padding:14px 28px;border-radius:14px;font-weight:900;cursor:pointer;text-transform:uppercase;font-size:12px;letter-spacing:1px;box-shadow:0 8px 24px rgba(255,106,0,0.3);">
          Sincronizar Aplicativo
        </button>
      </div>
    `;
  }
}
